import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead, SeoFunnelLink } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import {
  Phone,
  MapPin,
  Clock,
  ExternalLink,
  AlertCircle,
  Shield,
  ArrowRight,
} from "lucide-react";

/**
 * Static data hoisted outside component (React best practice: rendering-hoist-jsx)
 */
const CONTACTS = [
  {
    title: "Nacka kommun Servicecenter",
    description: "För allmänna frågor om hemtjänst, ansökan och kundval.",
    phone: "08-718 80 00",
    hours: "Vardagar 08:00–17:00",
    icon: Phone,
    primary: true,
  },
  {
    title: "Biståndsenheten",
    description: "För frågor om pågående ärenden och beslut.",
    phone: "08-718 80 00",
    hours: "Vardagar 08:00–17:00",
    note: 'Välj "Omsorg och stöd" i telefonmenyn',
    icon: Phone,
  },
  {
    title: "Anhörigstöd Nacka",
    description: "Stöd och rådgivning för dig som är anhörig.",
    phone: "08-718 80 00",
    hours: "Vardagar 08:00–17:00",
    icon: Phone,
  },
];

const USEFUL_LINKS = [
  {
    title: "Nacka kommun – Hemtjänst",
    url: "https://www.nacka.se/omsorg-stod/senior/hjalp-i-hemmet/hemtjanst/",
    description: "Officiell information och ansökan",
  },
  {
    title: "1177 Vårdguiden",
    url: "https://www.1177.se",
    description: "Sjukvårdsrådgivning dygnet runt",
  },
  {
    title: "Försäkringskassan",
    url: "https://www.forsakringskassan.se",
    description: "Information om vårdbidrag och merkostnader",
  },
  {
    title: "Socialstyrelsen",
    url: "https://www.socialstyrelsen.se",
    description: "Regler och riktlinjer för äldreomsorg",
  },
];

/**
 * Kontakt - Contact information page
 *
 * Lists all relevant contact points for Nacka home care
 */
const Kontakt: React.FC = () => {
  // Memoize schema generation (React best practice: rerender-memo)
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Kontakta Nacka Hemtjänst | Kontaktinformation och resurser",
        description:
          "Kontaktuppgifter till Nacka kommun och andra viktiga resurser för dig som söker hemtjänst i Nacka.",
        url: "https://nackahemtjanst.se/kontakt",
        datePublished: "2025-12-22",
        dateModified: "2026-01-09",
      }),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        { name: "Kontakt", url: "https://nackahemtjanst.se/kontakt" },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, breadcrumbSchema]),
    [articleSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Kontakta Nacka Hemtjänst | Kontaktinformation och resurser"
        description="Kontaktuppgifter till Nacka kommun och andra viktiga resurser för dig som söker hemtjänst i Nacka."
        canonicalUrl="https://nackahemtjanst.se/kontakt"
        ogType="article"
        keywords={["kontakt Nacka hemtjänst", "Nacka kommun hemtjänst"]}
        contentClassification="Consumer"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-01-09T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero with Glass Effect */}
      <section className="relative bg-gradient-to-b from-surface-100 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 via-transparent to-blue-100/20" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <Phone className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 tracking-tight mb-6">
            Kontakt
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed">
            Här hittar du kontaktuppgifter till Nacka kommun och andra viktiga
            resurser för dig som söker hemtjänst.
          </p>
        </div>
      </section>

      {/* Contact Cards with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-8 text-center">
            Ring Nacka kommun
          </h2>
          <div className="grid md:grid-cols-1 gap-6 max-w-2xl mx-auto">
            {CONTACTS.map((contact, index) => (
              <div
                key={index}
                className={`bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 md:p-8 shadow-glass-light hover:shadow-glass-hover transition-all ${
                  contact.primary ? "bg-emerald-50/50" : ""
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-surface-900 mb-2">
                      {contact.title}
                    </h3>
                    <p className="text-surface-600 mb-3">
                      {contact.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-surface-500">
                      <Clock className="w-4 h-4" />
                      {contact.hours}
                    </div>
                    {contact.note && (
                      <p className="text-sm text-surface-500 mt-2 italic">
                        {contact.note}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <a
                      href={`tel:${contact.phone.replace(/\s/g, "")}`}
                      className={`inline-flex items-center gap-2 px-6 py-4 rounded-xl text-lg font-bold transition-all ${
                        contact.primary
                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                          : "bg-surface-200 text-surface-800 hover:bg-surface-300"
                      }`}
                    >
                      <Phone className="w-5 h-5" />
                      {contact.phone}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visit in Person with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl shadow-glass-light p-8">
            <div className="flex items-start gap-4">
              <MapPin className="w-8 h-8 text-surface-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-surface-900 mb-4">
                  Besök Nacka stadshus
                </h2>
                <p className="text-surface-600 mb-4">
                  Du kan också besöka Nacka stadshus personligen för att få
                  hjälp med frågor om hemtjänst.
                </p>
                <address className="not-italic text-surface-700">
                  <strong>Nacka stadshus</strong>
                  <br />
                  Granitvägen 15
                  <br />
                  131 81 Nacka
                </address>
                <div className="mt-4 flex items-center gap-2 text-sm text-surface-500">
                  <Clock className="w-4 h-4" />
                  Öppet vardagar 08:00–17:00
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Useful Links with Glass Effect */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-8 text-center">
            Användbara länkar
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Internal link to Rights page */}
            <Link
              to="/rattigheter"
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <Shield className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-purple-600 transition-colors flex items-center gap-2">
                  Dina rättigheter
                  <ArrowRight className="w-4 h-4" />
                </h3>
                <p className="text-sm text-surface-600">
                  Läs mer om dina rättigheter som hemtjänsttagare
                </p>
              </div>
            </Link>
            {USEFUL_LINKS.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
              >
                <ExternalLink className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-surface-900 hover:text-blue-600 transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-sm text-surface-600">{link.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Observera</h3>
                <p className="text-amber-800 text-sm">
                  Denna webbplats är en oberoende guide och inte en del av Nacka
                  kommun. För officiell information och tjänster, kontakta
                  alltid Nacka kommun direkt via kontaktuppgifterna ovan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About This Site with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
            <h2 className="text-2xl font-bold text-surface-900 mb-4">
              Om denna webbplats
            </h2>
            <p className="text-surface-600 mb-6 max-w-2xl mx-auto">
              Nacka Hemtjänstguide drivs av{" "}
              <a
                href="https://www.eirtech.ai"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Eirtech
              </a>
              , som utvecklar{" "}
              <SeoFunnelLink
                href="https://www.caire.se/losningar/privat-hemtjanst"
                target="_blank"
                rel="noreferrer"
                tracking={{
                  sourceSite: "nackahemtjanst.se",
                  sourcePath: "/kontakt",
                  targetPath: "/losningar/privat-hemtjanst",
                  anchorCluster: "planeringssystem för hemtjänst",
                  ctaType: "editorial",
                }}
                className="text-emerald-600 font-medium hover:underline"
              >
                Caire
              </SeoFunnelLink>{" "}
              – en AI-plattform för hemtjänst. Vårt mål är att göra det enklare
              för brukare och anhöriga att hitta rätt omsorg.
            </p>
            <p className="text-sm text-surface-500">
              Har du frågor om webbplatsen? Kontakta oss via{" "}
              <SeoFunnelLink
                href="https://www.caire.se/kontakt"
                tracking={{
                  sourceSite: "nackahemtjanst.se",
                  sourcePath: "/kontakt",
                  targetPath: "/kontakt",
                  anchorCluster: "hemtjänst planeringsverktyg",
                  ctaType: "cta",
                }}
                className="text-emerald-600 hover:underline"
              >
                caire.se
              </SeoFunnelLink>
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Kontakt;
